import React, { useState, useEffect, useMemo } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import PlusMinQty from "../../utilities/PlusMinQty";
import { Info, X } from "lucide-react";
import { fmtINR } from "../../../util/helper";
import { Checkbox } from 'antd';

const defSLTP = ["Comb Prem", "PNL"];

const SLTPModalBasket = ({ show, setShow, defBasket, tickData, combPrem, info, setRmsData, popUpData }) => {
  const [formData, setFormData] = useState({
    sltpType: "PNL",
    stopLossAbsolute: null,
    takeProfitAbsolute: null,
    trailReferenceAbsolute: null,
    trailStepAbsolute: null,
    profitLockAt: null,
    profitLockSl: null,
    profitLockTrailRef: null,
    profitLockTrailStep: null
  });
  const [perLot, setPerLot] = useState(false);
  const [showScaleWarning, setShowScaleWarning] = useState(false);
  const [displayCombPrem, setDisplayCombPrem] = useState(0);
  const [errorMessages, setErrorMessages] = useState({
    stopLoss: "",
    target: "",
    trailSL: "",
    profitLock: "",
    profitLockTrail: ""
  });
  const [slValueType, setSlValueType] = useState(0);
  const [tpValueType, setTpValueType] = useState(0);
  const [trailValueType, setTrailValueType] = useState(2);


  useEffect(() => {
    if (popUpData) {
      setFormData({
        ...popUpData,
        sltpType: popUpData?.strategyRms?.basis === 2 ? "Underlying" : (popUpData?.strategyRms?.basis === 1 ? "Comb Prem" : "PNL"),
        stopLossAbsolute: popUpData?.strategyRms?.stopLossInput?.value || null,
        takeProfitAbsolute: popUpData?.strategyRms?.takeProfitInput?.value || null,
        trailReferenceAbsolute: popUpData?.strategyRms?.trailSL?.reference || null,
        trailStepAbsolute: popUpData?.strategyRms?.trailSL?.step || null,
        profitLockAt: popUpData?.strategyRms?.profitLockAt || null,
        profitLockSl: popUpData?.strategyRms?.profitLockSl || null,
        profitLockTrailRef: popUpData?.strategyRms?.profitLockTrail?.reference || null,
        profitLockTrailStep: popUpData?.strategyRms?.profitLockTrail?.step || null,
      });
      setSlValueType(popUpData?.strategyRms?.stopLossInput?.valueType ?? 0);
      setTpValueType(popUpData?.strategyRms?.takeProfitInput?.valueType ?? 0);
      setTrailValueType(popUpData?.strategyRms?.trailSL?.valueType ?? 2);
      setPerLot(!!popUpData?.strategyRms?.perLot);
    }
  }, [popUpData]);

  useEffect(() => {
    const data = popUpData?.row?.data || defBasket;
    if (data && data.length > 0) {
      let buyPremium = 0;
      let sellPremium = 0;
      for (let i = 0; i < data.length; i++) {
        const leg = data[i] || {};
        const netQty = parseFloat(leg.netQuantity || 0);
        const qty = Math.abs(netQty || parseFloat(leg.quantity || 0) || 0);
        const lotSize = parseFloat(leg.instrument?.lotSize || leg.lotSize || 1);
        const lots = lotSize > 0 ? (qty / lotSize) : 0;
        const token = leg.instrumentToken || leg.instrument?.instrumentToken;
        const ltp = parseFloat(leg.ltp || tickData?.[token]?.lp || 0);

        const sideInput = leg.transactionType || leg.side || (netQty < 0 ? "SELL" : netQty > 0 ? "BUY" : null);
        const side = sideInput ? String(sideInput).toUpperCase() : null;

        if (side) {
          if (perLot) {
            if (side === "BUY") buyPremium += ltp;
            else if (side === "SELL") sellPremium += ltp;
          } else if (lots > 0) {
            if (side === "BUY") buyPremium += ltp * lots;
            else if (side === "SELL") sellPremium += ltp * lots;
          }
        }
      }
      setDisplayCombPrem(buyPremium - sellPremium);
    } else {
      setDisplayCombPrem(combPrem || 0);
    }
  }, [popUpData, defBasket, tickData, perLot, combPrem]);

  const calculateActualPrice = (value, valueType, basePrice, isStopLoss = false) => {
    if (value === null || value === undefined || value === '' || value === 0) return null;
    const val = parseFloat(value);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    let result;
    switch (valueType) {
      case 0:
        result = val;
        break;
      case 1:
        if (formData.sltpType === "Comb Prem" || formData.sltpType === "PNL") {
          result = isStopLoss ? base - (Math.abs(base) * val / 100) : base + (Math.abs(base) * val / 100);
        } else {
          result = base + (base * val / 100);
        }
        break;
      case 2:
        if (formData.sltpType === "Comb Prem" || formData.sltpType === "PNL") {
          result = isStopLoss ? base - val : base + val;
        } else {
          result = isStopLoss ? -val : val;
        }
        break;
      default:
        result = val;
    }

    return result;
  };

  const getBasePrice = () => {
    const basisIsPNL = formData.sltpType === "PNL";
    return basisIsPNL ? 0 : parseFloat(displayCombPrem);
  };

  // Calculate actual prices using useMemo
  const basePrice = useMemo(() => getBasePrice(), [formData.sltpType, displayCombPrem]);
  const slActualPrice = useMemo(() =>
    calculateActualPrice(formData.stopLossAbsolute, slValueType, basePrice, true),
    [formData.stopLossAbsolute, slValueType, basePrice, formData.sltpType, displayCombPrem]
  );
  const tpActualPrice = useMemo(() =>
    calculateActualPrice(formData.takeProfitAbsolute, tpValueType, basePrice, false),
    [formData.takeProfitAbsolute, tpValueType, basePrice, formData.sltpType, combPrem]
  );
  const trailRefActualPrice = useMemo(() => {
    if (formData.trailReferenceAbsolute === null || formData.trailReferenceAbsolute === undefined || formData.trailReferenceAbsolute === '') {
      return null;
    }
    const val = parseFloat(formData.trailReferenceAbsolute);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    if (trailValueType === 1) {
      return base * val / 100;
    } else {
      return val;
    }
  }, [formData.trailReferenceAbsolute, trailValueType, basePrice]);

  const trailStepActualPrice = useMemo(() => {
    if (formData.trailStepAbsolute === null || formData.trailStepAbsolute === undefined || formData.trailStepAbsolute === '') {
      return null;
    }
    const val = parseFloat(formData.trailStepAbsolute);
    if (isNaN(val)) return null;

    const base = parseFloat(basePrice);
    if (isNaN(base)) return null;

    if (trailValueType === 1) {
      return base * val / 100;
    } else {
      return val;
    }
  }, [formData.trailStepAbsolute, trailValueType, basePrice]);

  const handleReset = () => {
    if (popUpData) {
      const resetFormData = {
        ...popUpData,
        sltpType: popUpData?.strategyRms?.basis === 2 ? "Underlying" : (popUpData?.strategyRms?.basis === 1 ? "Comb Prem" : "PNL"),
        stopLossAbsolute: null,
        takeProfitAbsolute: null,
        trailReferenceAbsolute: null,
        trailStepAbsolute: null,
        profitLockAt: null,
        profitLockSl: null,
        profitLockTrailRef: null,
        profitLockTrailStep: null,
      };
      setFormData(resetFormData);
      setErrorMessages({
        stopLoss: "",
        target: "",
        trailSL: "",
        profitLock: "",
        profitLockTrail: ""
      });
      const basisInit = resetFormData.sltpType;
      if (basisInit === "Comb Prem") {
        setSlValueType(2);
        setTpValueType(2);
        setTrailValueType(2);
      } else {
        setSlValueType(0);
        setTpValueType(0);
        setTrailValueType(2);
      }
    } else {
      setFormData({
        sltpType: "PNL",
        stopLossAbsolute: null,
        takeProfitAbsolute: null,
        trailReferenceAbsolute: null,
        trailStepAbsolute: null,
        profitLockAt: null,
        profitLockSl: null,
        profitLockTrailRef: null,
        profitLockTrailStep: null
      });
      setErrorMessages({
        stopLoss: "",
        target: "",
        trailSL: "",
        profitLock: "",
        profitLockTrail: ""
      });
      setSlValueType(0);
      setTpValueType(0);
      setTrailValueType(2);
    }
  };

  const validateTrailSL = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    // Both must be provided or both must be null
    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, trailSL: "Both fields are required" }));
      return false;
    }

    // If both are provided, they must be non-negative
    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, trailSL: "Trail SL Reference and Step must be non-negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    return true;
  };

  const validateProfitLock = (profitLockAt, profitLockSl) => {
    const atVal = profitLockAt !== null && profitLockAt !== undefined && profitLockAt !== '' ? parseFloat(profitLockAt) : null;
    const slVal = profitLockSl !== null && profitLockSl !== undefined && profitLockSl !== '' ? parseFloat(profitLockSl) : null;

    if (atVal !== null && slVal === null) {
      setErrorMessages((prev) => ({ ...prev, profitLock: "Both fields are required" }));
      return false;
    }

    setErrorMessages((prev) => ({ ...prev, profitLock: "" }));
    return true;
  };

  const validateProfitLockTrail = (ref, step) => {
    const refVal = ref !== null && ref !== undefined && ref !== '' ? parseFloat(ref) : null;
    const stepVal = step !== null && step !== undefined && step !== '' ? parseFloat(step) : null;

    // Both must be provided or both must be null
    if ((refVal !== null && stepVal === null) || (refVal === null && stepVal !== null)) {
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Both fields are required" }));
      return false;
    }

    // If both are provided, they must be non-negative
    if (refVal !== null && stepVal !== null) {
      if (refVal < 0 || stepVal < 0) {
        setErrorMessages((prev) => ({ ...prev, profitLockTrail: "Profit Trail values cannot be negative" }));
        return false;
      }
    }

    setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    return true;
  };


  const handlePerLotChange = (e) => {
    const checked = e.target.checked;
    setPerLot(checked);
    if (checked && (formData.stopLossAbsolute || formData.takeProfitAbsolute)) {
      setShowScaleWarning(true);
    } else if (!checked) {
      setShowScaleWarning(false);
    }
  };

  useEffect(() => {
    if (!formData.stopLossAbsolute && !formData.takeProfitAbsolute) {
      setShowScaleWarning(false);
    }
  }, [formData.stopLossAbsolute, formData.takeProfitAbsolute]);

  const handleFieldChange = (val, name) => {

    if (name === "stopLossAbsolute") {
      isValidsltp("sl", val);
    }
    if (name === "takeProfitAbsolute") {
      isValidsltp("tp", val);
    }

    let value = {
      ...formData,
      [name]: !val ? val : parseFloat(val),
    };

    if (name === "stopLossAbsolute" && (!val || val === 0 || val === null)) {
      value.trailReferenceAbsolute = null;
      value.trailStepAbsolute = null;
      setErrorMessages((prev) => ({ ...prev, trailSL: "" }));
    }

    if ((name === "profitLockAt" || name === "profitLockSl") && (!val || val === 0 || val === null)) {
      value.profitLockTrailRef = null;
      value.profitLockTrailStep = null;
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
    }

    // Validate trail SL when trail fields change (don't return early - allow state update)
    if (name === "trailReferenceAbsolute" || name === "trailStepAbsolute") {
      const newRef = name === "trailReferenceAbsolute" ? value.trailReferenceAbsolute : formData.trailReferenceAbsolute;
      const newStep = name === "trailStepAbsolute" ? value.trailStepAbsolute : formData.trailStepAbsolute;
      validateTrailSL(newRef, newStep);
    }

    // Validate profit lock when profit lock fields change
    if (name === "profitLockAt" || name === "profitLockSl") {
      const newAt = name === "profitLockAt" ? value.profitLockAt : formData.profitLockAt;
      const newSl = name === "profitLockSl" ? value.profitLockSl : formData.profitLockSl;
      validateProfitLock(newAt, newSl);
    }

    // Validate profit lock trail when trail fields change
    if (name === "profitLockTrailRef" || name === "profitLockTrailStep") {
      const newRef = name === "profitLockTrailRef" ? value.profitLockTrailRef : formData.profitLockTrailRef;
      const newStep = name === "profitLockTrailStep" ? value.profitLockTrailStep : formData.profitLockTrailStep;
      validateProfitLockTrail(newRef, newStep);
    }

    setFormData(value);
  };

  const cycleWithin = (current, allowed) => {
    const idx = allowed.indexOf(current);
    const nextIdx = (idx + 1) % allowed.length;
    return allowed[nextIdx];
  };

  const renderTypeLabel = (vt) => (vt === 0 ? "₹" : vt === 1 ? "%" : "Pts");
  const renderTrailTypeLabel = (vt) => (vt === 1 ? "%" : "Pts");
  const renderPNLLabel = () => "₹₹";
  const renderPNLSLTPLabel = (vt) => (vt === 0 ? "₹₹" : "Pts");

  const onClickSlType = () => {
    const basis = formData?.sltpType;
    const newSlValueType = basis === "PNL" ?
      cycleWithin(slValueType, [0, 2]) :
      cycleWithin(slValueType, [0, 1, 2]);

    setSlValueType(newSlValueType);
    isValidsltp("sl", formData?.stopLossAbsolute, newSlValueType);
  };

  const onClickTpType = () => {
    const basis = formData?.sltpType;
    const newTpValueType = basis === "PNL" ?
      cycleWithin(tpValueType, [0, 2]) :
      cycleWithin(tpValueType, [0, 1, 2]);

    setTpValueType(newTpValueType);
    isValidsltp("tp", formData?.takeProfitAbsolute, newTpValueType);
  };

  const onClickTrailType = () => {
    const basis = formData?.sltpType;
    if (basis === "PNL") {
      setTrailValueType(2);
      return;
    }
    setTrailValueType(prev => cycleWithin(prev, [2, 1]));
  };

  const isValidsltp = (type, val, overrideType = null) => {
    const valueType = overrideType ?? (type === "sl" ? slValueType : tpValueType);
    const setMsg = (ok, customMsg = null) => {
      if (type === "sl") {
        const msg = customMsg || (formData.sltpType === "PNL" ? "Stop Loss should be below current PNL" : "Stop Loss should be below combined premium");
        setErrorMessages((prev) => ({ ...prev, stopLoss: ok ? "" : msg }));
      } else {
        const msg = customMsg || (formData.sltpType === "PNL" ? "Target should be above current PNL" : "Target should be above combined premium");
        setErrorMessages((prev) => ({ ...prev, target: ok ? "" : msg }));
      }
    };

    if (val === null || val === undefined || val === '' || val === 0) {
      setMsg(true);
      return true;
    }

    const parsedVal = parseFloat(val);
    if (Number.isNaN(parsedVal)) {
      setMsg(true);
      return true;
    }

    const actualPrice = type === "sl"
      ? calculateActualPrice(parsedVal, valueType, getBasePrice(), true)
      : calculateActualPrice(parsedVal, valueType, getBasePrice(), false);

    if (actualPrice === null) {
      setMsg(true);
      return true;
    }

    /* if (formData.sltpType === "Comb Prem") {
      if (actualPrice <= 0) {
        setMsg(false, type === "sl" ? "Stop Loss must be positive" : "Target must be positive");
        return false;
      }
    } */


    if (formData.sltpType === "PNL") {
      if (valueType === 2 && val < 0) {
        setMsg(false, type === "sl" ? "Stop Loss cannot be negative" : "Target cannot be negative");
        return false;
      }
    }

    if (formData.sltpType === "PNL" || formData.sltpType === "Comb Prem") {
      const ref = formData.sltpType === "PNL" ? parseFloat(popUpData?.totalPNL || 0) : parseFloat(combPrem);
      if (Number.isNaN(ref)) {
        setMsg(true);
        return true;
      }

      if (type === "sl") {
        const ok = actualPrice < ref;
        setMsg(ok);
        return ok;
      } else {
        const ok = actualPrice > ref;
        setMsg(ok);
        return ok;
      }
    } else {
      return true;
    }
  };

  const clickActionSave = () => {
    if (!isValidsltp("sl", parseFloat(formData.stopLossAbsolute))) {
      return;
    }
    if (!isValidsltp("tp", parseFloat(formData.takeProfitAbsolute))) {
      return;
    }

    // Validate trail SL
    if (!validateTrailSL(formData.trailReferenceAbsolute, formData.trailStepAbsolute)) {
      return;
    }

    // Validate profit lock
    if (!validateProfitLock(formData.profitLockAt, formData.profitLockSl)) {
      return;
    }

    // Validate profit lock trail
    if (!validateProfitLockTrail(formData.profitLockTrailRef, formData.profitLockTrailStep)) {
      return;
    }

    const basis = formData?.sltpType;
    const basisValue = basis === "Comb Prem" ? 1 : 0;
    const norm = (n) => {
      if (n === 0) return 0;
      if (n === undefined || n === null || n === "") return null;
      const p = parseFloat(n);
      return Number.isNaN(p) ? null : p;
    };

    const isTrailNull = (ref, step) => {
      const refVal = norm(ref);
      const stepVal = norm(step);
      return refVal === null && stepVal === null;
    };

    const isValueNull = (val) => norm(val) === null;

    const rmsPayload = {
      basis: basisValue,
      takeProfitInput: isValueNull(formData.takeProfitAbsolute) ? null : {
        valueType: tpValueType ?? 0,
        value: norm(formData.takeProfitAbsolute)
      },
      stopLossInput: isValueNull(formData.stopLossAbsolute) ? null : {
        valueType: slValueType ?? 0,
        value: norm(formData.stopLossAbsolute)
      },
      trailSL: isTrailNull(formData.trailReferenceAbsolute, formData.trailStepAbsolute) ? null : {
        valueType: (trailValueType === 1 ? 1 : 2),
        reference: norm(formData.trailReferenceAbsolute),
        step: norm(formData.trailStepAbsolute)
      },
      profitLockAt: norm(formData.profitLockAt),
      profitLockSl: norm(formData.profitLockSl),
      profitLockTrail: isTrailNull(formData.profitLockTrailRef, formData.profitLockTrailStep) ? null : {
        valueType: 2,
        reference: norm(formData.profitLockTrailRef),
        step: norm(formData.profitLockTrailStep)
      }
    };
    if (basisValue === 1 && perLot) {
      rmsPayload.perLot = true;
    }
    const payload = { rms: rmsPayload };

    setRmsData(payload);
    setShow(false);
  };

  const clearfiled = (name) => {
    let updatedFormData = {
      ...formData,
      [name]: null
    };

    if (name === "stopLossAbsolute") {
      updatedFormData.trailReferenceAbsolute = null;
      updatedFormData.trailStepAbsolute = null;
      setErrorMessages((prev) => ({ ...prev, stopLoss: "", trailSL: "" }));
    } else if (name === "profitLockAt" || name === "profitLockSl") {
      updatedFormData.profitLockTrailRef = null;
      updatedFormData.profitLockTrailStep = null;
      setErrorMessages((prev) => ({ ...prev, profitLockTrail: "" }));
      if (name === "takeProfitAbsolute") {
        setErrorMessages((prev) => ({ ...prev, target: "" }));
      }
    } else if (name === "takeProfitAbsolute") {
      setErrorMessages((prev) => ({ ...prev, target: "" }));
    }
    // Validate trail SL when one field is cleared
    else if (name === "trailReferenceAbsolute") {
      const otherValue = updatedFormData.trailStepAbsolute;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateTrailSL(null, otherValue);
      } else {
        setErrorMessages(prev => ({ ...prev, trailSL: "" }));
      }
    }
    else if (name === "trailStepAbsolute") {
      const otherValue = updatedFormData.trailReferenceAbsolute;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateTrailSL(otherValue, null);
      } else {
        setErrorMessages(prev => ({ ...prev, trailSL: "" }));
      }
    }
    // Validate profit lock trail when one field is cleared
    else if (name === "profitLockTrailRef") {
      const otherValue = formData.profitLockTrailStep;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateProfitLockTrail(null, otherValue);
      } else {
        setErrorMessages(prev => ({ ...prev, profitLockTrail: "" }));
      }
    }
    else if (name === "profitLockTrailStep") {
      const otherValue = formData.profitLockTrailRef;
      if (otherValue !== null && otherValue !== undefined && otherValue !== '') {
        validateProfitLockTrail(otherValue, null);
      } else {
        setErrorMessages(prev => ({ ...prev, profitLockTrail: "" }));
      }
    }

    setFormData(updatedFormData);
  };

  const handleSltpTypeChange = (e) => {
    const val = e.target.value;
    if (formData.sltpType === val) {
      setFormData({ ...formData, sltpType: val })
    } else {
      setFormData({
        "sltpType": val,
        "stopLossAbsolute": null,
        "takeProfitAbsolute": null,
        "trailReferenceAbsolute": null,
        "trailStepAbsolute": null,
        "profitLockAt": null,
        "profitLockSl": null,
        "profitLockTrailRef": null,
        "profitLockTrailStep": null
      })
      setSlValueType(0);
      setTpValueType(0);
      setTrailValueType(2);
      setErrorMessages({ stopLoss: "", target: "", trailSL: "", profitLock: "", profitLockTrail: "" });
      // Reset perLot when switching away from Premium
      if (val !== "Comb Prem") {
        setPerLot(false);
      }
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        size="sm"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="fixed-modal-sltp "
      >
        <Modal.Header style={{ position: 'relative' }}>
          <Modal.Title
            className="modal-title-size"
            id="contained-modal-title-vcenter"
          >
            Basket Risk Settings
          </Modal.Title>
          <button
            type="button"
            className="modal-close-button"
            onClick={() => setShow(false)}
            aria-label="Close"
          >
            ×
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="modal-sltp">
            <div style={{ justifyContent: "space-between" }} className="sltp-div modal-input ">
              <Form.Group className="form-group">
                <label className="form-label">SL/TP Basis</label>
                <Form.Select
                  aria-label="Default select example"
                  name="sltpType"
                  value={formData.sltpType}
                  onChange={handleSltpTypeChange}
                >
                  {defSLTP.map((data, index) => {
                    return (
                      <option value={data} key={index}>
                        {data}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
              {formData.sltpType === "Comb Prem" && (
                <Checkbox
                  id="perLot-basket-terminal"
                  checked={perLot}
                  onChange={handlePerLotChange}
                  style={{ fontSize: '11px', marginLeft: '8px', whiteSpace: 'nowrap', width: '100px' }}
                >
                  1 Lot RMS
                </Checkbox>
              )}
              <div className="pnl-com-p">
                {formData.sltpType === "PNL" ?
                  <p className="m-0">PNL: <span className="text-success">0.00</span></p>
                  : <p className="m-0">Comb Prem: <span style={{ color: "inherit !important" }}>{displayCombPrem.toFixed(2)}</span></p>
                }
              </div>
            </div>

            {showScaleWarning && (
              <div className="info-note mb-2" style={{ fontSize: '10px', padding: '8px 10px' }}>
                <Info size={12} style={{ color: '#faad14', marginRight: '4px' }} />
                <span>Scale changed! Please re-verify Absolute SL/TP (Values are now Per Lot).</span>
              </div>
            )}
            <div className="sltp-div">
              <div className="sltp-col-left">
                <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                  Stop Loss
                  {slActualPrice !== null && (slValueType === 1 || slValueType === 2) && (
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                      ({fmtINR(slActualPrice.toFixed(2))})
                    </span>
                  )}
                </Form.Label>
                <div className="sltp-input-row">
                  <div className={errorMessages.stopLoss ? "error-border" : ""}>
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "stopLossAbsolute");
                      }}
                      filterValue={formData.stopLossAbsolute}
                      name={"stopLossAbsolute"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class" onClick={onClickSlType}>{renderPNLSLTPLabel(slValueType)}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickSlType}>{renderTypeLabel(slValueType)}</div>
                  )}
                  <X
                    size={16}
                    onClick={() => clearfiled("stopLossAbsolute")}
                    className="sltp-delete-icon sltp-delete-icon-enabled"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                {errorMessages.stopLoss && (
                  <div className="text-danger mt-1" style={{ fontSize: '10px' }}>
                    {errorMessages.stopLoss}
                  </div>
                )}
              </div>
              <div className="sltp-col-right">
                <Form.Label className="sltp-label-above" style={{ fontWeight: 600 }}>
                  Target
                  {tpActualPrice !== null && (tpValueType === 1 || tpValueType === 2) && (
                    <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                      ({fmtINR(tpActualPrice.toFixed(2))})
                    </span>
                  )}
                </Form.Label>
                <div className="sltp-input-row">
                  <div className={errorMessages.target ? "error-border" : ""}>
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "takeProfitAbsolute");
                      }}
                      filterValue={formData.takeProfitAbsolute}
                      name={"takeProfitAbsolute"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                  </div>
                  {formData?.sltpType === "PNL" ? (
                    <div className="pts-class" onClick={onClickTpType}>{renderPNLSLTPLabel(tpValueType)}</div>
                  ) : (
                    <div className="pts-class" onClick={onClickTpType}>{renderTypeLabel(tpValueType)}</div>
                  )}
                  <X
                    size={16}
                    onClick={() => clearfiled("takeProfitAbsolute")}
                    className="sltp-delete-icon sltp-delete-icon-enabled"
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                {errorMessages.target && (
                  <div className="text-danger mt-1" style={{ fontSize: '10px' }}>
                    {errorMessages.target}
                  </div>
                )}
              </div>
            </div>
            <div className="sltp-div-profile">
              <p className="sltp-trail-header">Stoploss Trailing</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">
                    For every X move
                    {trailRefActualPrice !== null && typeof trailRefActualPrice === 'number' && (
                      <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                        (₹{trailRefActualPrice.toFixed(2)})
                      </span>
                    )}
                  </Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "trailReferenceAbsolute");
                      }}
                      filterValue={formData.trailReferenceAbsolute || null}
                      name={"trailReferenceAbsolute"}
                      min={0.05}
                      step={0.05}
                      disabled={!formData.stopLossAbsolute || formData.stopLossAbsolute === null}
                      formatINR={true}
                    />
                    {(formData?.sltpType === "PNL") ? (
                      <div className="pts-class">{renderPNLLabel()}</div>
                    ) : (
                      <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                    )}
                    <X
                      size={16}
                      onClick={() => clearfiled("trailReferenceAbsolute")}
                      className={`sltp-delete-icon ${(!formData.stopLossAbsolute || formData.stopLossAbsolute === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">
                    Trail SL by Y
                    {trailStepActualPrice !== null && typeof trailStepActualPrice === 'number' && (
                      <span style={{ marginLeft: '10px', fontSize: '11px', color: '#666', fontWeight: 'normal' }}>
                        (₹{trailStepActualPrice.toFixed(2)})
                      </span>
                    )}
                  </Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "trailStepAbsolute");
                      }}
                      filterValue={formData.trailStepAbsolute || null}
                      name={"trailStepAbsolute"}
                      min={0.05}
                      step={0.05}
                      disabled={!formData.stopLossAbsolute || formData.stopLossAbsolute === null}
                      formatINR={true}
                    />
                    {(formData?.sltpType === "PNL") ? (
                      <div className="pts-class">{renderPNLLabel()}</div>
                    ) : (
                      <div className="pts-class" onClick={onClickTrailType}>{renderTrailTypeLabel(trailValueType)}</div>
                    )}
                    <X
                      size={16}
                      onClick={() => clearfiled("trailStepAbsolute")}
                      className={`sltp-delete-icon ${(!formData.stopLossAbsolute || formData.stopLossAbsolute === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
              {errorMessages.trailSL && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.trailSL}
                </div>
              )}
              <p className="sltp-trail-header">Profit Locking</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">When profit reaches</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "profitLockAt");
                      }}
                      filterValue={formData.profitLockAt || null}
                      name={"profitLockAt"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X
                      size={16}
                      onClick={() => clearfiled("profitLockAt")}
                      className="sltp-delete-icon sltp-delete-icon-enabled"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Lock minimum profit at</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "profitLockSl");
                      }}
                      filterValue={formData.profitLockSl || null}
                      name={"profitLockSl"}
                      min={null}
                      step={null}
                      max={null}
                      title={""}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X
                      size={16}
                      onClick={() => clearfiled("profitLockSl")}
                      className="sltp-delete-icon sltp-delete-icon-enabled"
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
              {errorMessages.profitLock && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.profitLock}
                </div>
              )}
              <p className="sltp-trail-header">Profit Trailing</p>
              <div className="sltp-div">
                <div className="sltp-col-left">
                  <Form.Label className="sltp-label-above">If profit increases by X</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "profitLockTrailRef");
                      }}
                      filterValue={formData.profitLockTrailRef || null}
                      name={"profitLockTrailRef"}
                      min={0.05}
                      step={0.05}
                      max={null}
                      title={""}
                      disabled={!formData.profitLockAt || !formData.profitLockSl || formData.profitLockAt === null || formData.profitLockSl === null}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X
                      size={16}
                      onClick={() => clearfiled("profitLockTrailRef")}
                      className={`sltp-delete-icon ${(!formData.profitLockAt || !formData.profitLockSl || formData.profitLockAt === null || formData.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
                <div className="sltp-col-right">
                  <Form.Label className="sltp-label-above">Trail Profit by Y</Form.Label>
                  <div className="sltp-input-row">
                    <PlusMinQty
                      qtyClick={(qty, value) => {
                        handleFieldChange(qty, "profitLockTrailStep");
                      }}
                      filterValue={formData.profitLockTrailStep || null}
                      name={"profitLockTrailStep"}
                      min={0.05}
                      step={0.05}
                      max={null}
                      title={""}
                      disabled={!formData.profitLockAt || !formData.profitLockSl || formData.profitLockAt === null || formData.profitLockSl === null}
                      formatINR={true}
                    />
                    <div className="pts-class">{renderPNLLabel()}</div>
                    <X
                      size={16}
                      onClick={() => clearfiled("profitLockTrailStep")}
                      className={`sltp-delete-icon ${(!formData.profitLockAt || !formData.profitLockSl || formData.profitLockAt === null || formData.profitLockSl === null) ? 'sltp-delete-icon-disabled' : 'sltp-delete-icon-enabled'}`}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              </div>
              {errorMessages.profitLockTrail && (
                <div className="text-danger mt-1 mb-2" style={{ fontSize: '10px', textAlign: 'center' }}>
                  {errorMessages.profitLockTrail}
                </div>
              )}
            </div>
          </div>
          {/* Summary Section */}
          <div className="sltp-summary-section">
            <div className="sltp-summary-title">
              Summary
            </div>
            <div className="sltp-summary-body">
              {(() => {
                const summaryLines = [];
                const basisLabel = formData?.sltpType === "PNL" ? "PnL" : "combined premium";

                // Stop Loss
                if (formData.stopLossAbsolute !== null && formData.stopLossAbsolute !== undefined && formData.stopLossAbsolute !== '') {
                  const slValue = parseFloat(formData.stopLossAbsolute);
                  let slText = '';
                  if (formData?.sltpType === "PNL") {
                    if (slValueType === 0) {
                      slText = `₹${Math.abs(slValue).toFixed(2)}`;
                    } else {
                      // For Pts, show actual price
                      if (slActualPrice !== null) {
                        slText = `₹${Math.abs(slActualPrice).toFixed(2)}`;
                      } else {
                        slText = `${Math.abs(slValue).toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} falls to ${slText}, the strategy will exit.`);
                  } else {
                    let slDisplay = '';
                    if (slValueType === 0) {
                      slDisplay = `₹${slValue.toFixed(2)}`;
                    } else if (slValueType === 1 || slValueType === 2) {
                      // For % and Pts, show actual price
                      if (slActualPrice !== null) {
                        slDisplay = `₹${slActualPrice.toFixed(2)}`;
                      } else {
                        slDisplay = slValueType === 1 ? `${slValue.toFixed(2)}%` : `${slValue.toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} falls to ${slDisplay}, the strategy will exit.`);
                  }
                }

                // Target
                if (formData.takeProfitAbsolute !== null && formData.takeProfitAbsolute !== undefined && formData.takeProfitAbsolute !== '') {
                  const tpValue = parseFloat(formData.takeProfitAbsolute);
                  let tpText = '';
                  if (formData?.sltpType === "PNL") {
                    if (tpValueType === 0) {
                      tpText = `₹${Math.abs(tpValue).toFixed(2)}`;
                    } else {
                      // For Pts, show actual price
                      if (tpActualPrice !== null) {
                        tpText = `₹${Math.abs(tpActualPrice).toFixed(2)}`;
                      } else {
                        tpText = `${Math.abs(tpValue).toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} rises to ${tpText}, the strategy will exit with profit.`);
                  } else {
                    let tpDisplay = '';
                    if (tpValueType === 0) {
                      tpDisplay = `₹${tpValue.toFixed(2)}`;
                    } else if (tpValueType === 1 || tpValueType === 2) {
                      // For % and Pts, show actual price
                      if (tpActualPrice !== null) {
                        tpDisplay = `₹${tpActualPrice.toFixed(2)}`;
                      } else {
                        tpDisplay = tpValueType === 1 ? `${tpValue.toFixed(2)}%` : `${tpValue.toFixed(2)} pts`;
                      }
                    }
                    summaryLines.push(`If ${basisLabel} rises to ${tpDisplay}, the strategy will exit with profit.`);
                  }
                }

                // Stoploss Trailing
                if ((formData.trailReferenceAbsolute !== null && formData.trailReferenceAbsolute !== undefined && formData.trailReferenceAbsolute !== '') &&
                  (formData.trailStepAbsolute !== null && formData.trailStepAbsolute !== undefined && formData.trailStepAbsolute !== '')) {
                  const refValue = parseFloat(formData.trailReferenceAbsolute);
                  const stepValue = parseFloat(formData.trailStepAbsolute);
                  let refText = '';
                  let stepText = '';

                  if (formData?.sltpType === "PNL") {
                    refText = `₹${refValue.toFixed(2)}`;
                    stepText = `₹${stepValue.toFixed(2)}`;
                  } else {
                    if (trailValueType === 1) {
                      // For %, show actual price if available
                      if (trailRefActualPrice !== null && trailStepActualPrice !== null) {
                        refText = `₹${trailRefActualPrice.toFixed(2)}`;
                        stepText = `₹${trailStepActualPrice.toFixed(2)}`;
                      } else {
                        refText = `${refValue.toFixed(2)}%`;
                        stepText = `${stepValue.toFixed(2)}%`;
                      }
                    } else {
                      refText = `₹${refValue.toFixed(2)}`;
                      stepText = `₹${stepValue.toFixed(2)}`;
                    }
                  }
                  summaryLines.push(`For every ${refText} increase in profit, SL will move up by ${stepText}.`);
                }

                // Profit Locking
                if ((formData.profitLockAt !== null && formData.profitLockAt !== undefined && formData.profitLockAt !== '') &&
                  (formData.profitLockSl !== null && formData.profitLockSl !== undefined && formData.profitLockSl !== '')) {
                  const atValue = parseFloat(formData.profitLockAt);
                  const slValue = parseFloat(formData.profitLockSl);
                  summaryLines.push(`When profit reaches ₹${atValue.toFixed(2)}, at least ₹${slValue.toFixed(2)} will be protected.`);
                }

                // Profit Trailing
                if ((formData.profitLockTrailRef !== null && formData.profitLockTrailRef !== undefined && formData.profitLockTrailRef !== '') &&
                  (formData.profitLockTrailStep !== null && formData.profitLockTrailStep !== undefined && formData.profitLockTrailStep !== '')) {
                  const refValue = parseFloat(formData.profitLockTrailRef);
                  const stepValue = parseFloat(formData.profitLockTrailStep);
                  summaryLines.push(`Each time profit increases by ₹${refValue.toFixed(2)}, locked profit will rise by ₹${stepValue.toFixed(2)}.`);
                }

                if (summaryLines.length === 0) {
                  return <div style={{ color: '#6b7785', fontStyle: 'italic' }}>No risk settings configured</div>;
                }

                return summaryLines.map((line, index) => (
                  <div key={index} style={{ marginBottom: index < summaryLines.length - 1 ? '8px' : '0' }}>
                    {line}
                  </div>
                ));
              })()}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleReset} variant="outline-primary">Reset</Button>
          <Button disabled={!!errorMessages.stopLoss || !!errorMessages.target || !!errorMessages.trailSL || !!errorMessages.profitLock || !!errorMessages.profitLockTrail} onClick={clickActionSave}>Save</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SLTPModalBasket;